/**
 * Context, result type, and consumer hook for {@link EntitlementsProvider}.
 *
 * Lifted out of `useEntitlements.tsx` to satisfy the
 * `react-refresh/only-export-components` rule (#2548): a `.tsx` file that
 * exports both a component and a hook breaks Fast Refresh because the
 * runtime cannot tell which export is the component to swap. The Provider
 * component stays in the `.tsx` file; the context + result type + consumer
 * hook live here.
 */
import { createContext, useContext } from 'react';
import type { EntitlementsResponse } from '../services/api';

/** Shape returned by {@link useEntitlements}. */
export interface UseEntitlementsResult {
    /** Whether the entitlements are still being fetched. */
    loading: boolean;
    /** The tenant's billing tier, or null while loading. */
    tier: EntitlementsResponse['tier'] | null;
    /** The tenant's feature flags, or null while loading. */
    features: EntitlementsResponse['features'] | null;
    /** Number of acknowledgments recorded in the current billing period. */
    currentPeriodAcknowledgments: number;
    /**
     * The `YYYY-MM-DD` anchor date for the current billing period. Incremented
     * annually on the tenant's signup anniversary. `null` when the API did not
     * return one (older records, or the permissive fallback on error). Callers
     * may safely ignore this — it is optional for backward compat with older
     * fixtures.
     */
    periodStartDate?: string | null;
    /**
     * Number of "User Pack" add-ons the tenant has purchased (#1630). Each
     * pack lifts `features.maxTotalUsers` by 50; the effective cap is already
     * baked into `features.maxTotalUsers`. UI uses this raw value to render
     * the "200 base + N packs × 50 = X effective cap" breakdown on the Plan
     * & Usage panel. `undefined` when the tenant has no pack add-ons (or the
     * API didn't return the field — e.g., the permissive fallback on error).
     */
    userPackQuantity?: number;
    /**
     * Whether the tenant has a Stripe customer / billing account (#2070).
     * Used by `PlanUsagePanel` to gate the Manage Billing button on
     * `tier === 'standard' && hasBillingAccount === true`. The hook always
     * supplies a value: `false` when the API omits the field (safe — hide
     * the button rather than show a button that 404s on click) and `true`
     * on the permissive error fallback (preserve today's click-and-toast
     * UX so a transient API failure doesn't hide a feature the user has).
     *
     * Optional in the result type for backward compat with the dozen test
     * mocks across the UI that pre-date this field (mirroring the
     * `userPackQuantity` and `refetch` precedent). Callers MUST gate on
     * explicit `=== true` rather than truthy-check because `undefined` from
     * a stale mock should default to "hidden".
     */
    hasBillingAccount?: boolean;
    /**
     * Re-fetch the entitlements from the API. The Plan & Usage panel calls
     * this after a successful "Buy more seats" save so the breakdown reflects
     * the new pack count without a page reload. Optional in the result type
     * for backward compat with existing test mocks that pre-date #1630; the
     * hook itself always provides a function.
     */
    refetch?: () => Promise<void>;
}

/**
 * Private context holding the single canonical entitlements value for the
 * authenticated session. Consumers never touch this directly — they read it
 * through {@link useEntitlements}, which throws if no provider is mounted.
 */
export const EntitlementsContext = createContext<UseEntitlementsResult | null>(null);

/**
 * Read the current tenant's entitlements from the surrounding
 * `EntitlementsProvider`. Returns the single canonical value fetched
 * once per session — no per-consumer fetch. Throws if called outside a
 * provider so misuse fails loudly at mount rather than silently fetching.
 */
export function useEntitlements(): UseEntitlementsResult {
    const ctx = useContext(EntitlementsContext);
    if (ctx === null) {
        throw new Error('useEntitlements must be used within an EntitlementsProvider');
    }
    return ctx;
}
